import { Flex, Table } from '@/components';
import styles from '@/features/public-site/_pages/SheetsListPage.module.scss';
import { t } from '@/i18n';
import { DownloadOutlined, FileOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { useMQ } from '../../hooks';
import { THEME } from '../../utils/constants';
import { download } from '../../utils/download';
import {
  type MinistriesType,
  addMinistryNamePrefix,
} from '../../utils/ministry';
import { Contents } from '../Contents';

type SheetTypeName = 'rs' | 'ks' | 'ksLocalGovernment' | 'ksInvestment';
const groupTitles: {
  [k in SheetTypeName]: string;
} = {
  rs: t('ps-sheets-page-sheet-type-name-rs'),
  ks: t('ps-sheets-page-sheet-type-name-ks'),
  ksLocalGovernment: t('ps-sheets-page-sheet-type-name-ks-local-government'),
  ksInvestment: t('ps-sheets-page-sheet-type-name-ks-investment'),
};

const sheetTitles = {
  rsFormat1: t('ps-sheet-title-rs-project-list-format1'),
  rsFormat2: t('ps-sheet-title-rs-project-list-format2'),
  rsFormat3: t('ps-sheet-title-rs-project-list-format3'),
  rsFormat4: t('ps-sheet-title-rs-summary-format4'),
  ks: t('ps-sheet-title-ks-fund-status-list'),
  ksLocalGovernmentA: t(
    'ps-sheet-title-ks-fund-status-list-of-local-government-basic-information',
  ),
  ksLocalGovernmentB: t(
    'ps-sheet-title-ks-fund-status-list-of-local-government-income-and-expenditure',
  ),
  ksInvestment: t('ps-sheet-title-ks-investment-status-list'),
};

export const ExportFileGroup: FC<{
  path: string;
  sheetTypeNameKey: SheetTypeName;
  dataSource: MinistriesType['files'];
  caption?: string;
}> = ({ path, sheetTypeNameKey, dataSource, caption }) => {
  const { sp } = useMQ();

  if (!dataSource?.length) {
    return null;
  }

  return (
    <Flex vertical gap={16} className={styles.group}>
      <Contents.Text
        text={groupTitles[sheetTypeNameKey]}
        weight="bold"
        className={styles.group__title}
        style={{
          color: THEME.colors.textSecondary,
        }}
      />
      <Flex vertical gap={8} className={styles.group__main}>
        <Table
          pagination={false}
          rowHoverable={false}
          dataSource={dataSource}
          rowKey="fileType"
          columns={[
            {
              title: (
                <Contents.Text
                  text={t('file-name')}
                  className={styles.group__columnTitle}
                />
              ),
              dataIndex: 'fileType',
              render: (_, { fileType, specificMinistry }) => (
                <Contents.Text
                  text={addMinistryNamePrefix(
                    sheetTitles[fileType],
                    specificMinistry,
                  )}
                />
              ),
            },
            {
              dataIndex: 'fileInfo',
              width: sp ? 100 : 240,
              align: 'right',
              render: (_, { fileName }) => {
                const csvFileName = `${fileName}.csv`;
                const csvUrl = `${path}${csvFileName}`;

                const pdfFileName = `${fileName}.pdf`;
                const pdfUrl = `${path}${pdfFileName}`;
                return (
                  <Flex
                    justify="flex-end"
                    align="center"
                    className={styles.group__buttons}
                  >
                    <Contents.LinkText
                      text="CSV"
                      onClick={() => {
                        download(csvUrl, csvFileName);
                      }}
                      beforeIcon={FileOutlined}
                      afterIcon={DownloadOutlined}
                      weight="bold"
                      className={styles.group__button}
                    />
                    <Contents.LinkText
                      text="PDF"
                      onClick={() => {
                        download(pdfUrl, pdfFileName);
                      }}
                      beforeIcon={FileOutlined}
                      afterIcon={DownloadOutlined}
                      weight="bold"
                      className={styles.group__button}
                    />
                  </Flex>
                );
              },
            },
          ]}
          style={{
            width: '100%',
          }}
          scroll={{
            x: undefined,
          }}
        />
        {caption && (
          <Contents.Text
            text={caption}
            className={styles.group__caption}
            style={{
              color: THEME.colors.textTertiary,
            }}
          />
        )}
      </Flex>
    </Flex>
  );
};
